import type { DataProvider, GetListParams, GetListResult } from 'react-admin'

import { discussionCsApi } from './chapterDiscussions.api'
import {
  buildCommentQuery,
  buildReportQuery,
  discussionCommentIdentityKey,
} from './chapterDiscussions.model'

import type {
  DiscussionCommentListItem,
  DiscussionReportListItem,
  ListDiscussionCommentsQuery,
  ListDiscussionReportsQuery,
  ModerationStatus,
  SerializedTimestamp,
  VisibilityStatus,
} from './chapterDiscussions.types'

export const CHAPTER_DISCUSSION_COMMENTS_RESOURCE =
  'chapter-discussion-comments'
export const CHAPTER_DISCUSSION_REPORTS_RESOURCE = 'chapter-discussion-reports'

export interface DiscussionCommentListRecord {
  id: string
  createdAt: string
  bookTitle: string
  chapterTitle: string
  commentBody: string
  authorId: number
  authorName: string
  reportCount: number
  moderationStatus: ModerationStatus
  visibilityStatus: VisibilityStatus
  source: DiscussionCommentListItem
}

export interface DiscussionReportListRecord {
  id: string
  commentIdentity: string
  createdAt: string
  bookTitle: string
  chapterTitle: string
  reporterId: number
  reporterName: string
  reason: DiscussionReportListItem['report']['reason']
  evidence: string
  status: DiscussionReportListItem['report']['status']
  source: DiscussionReportListItem
}

function optionalText(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value.trim() : undefined
}

function optionalNumber(value: unknown): number | undefined {
  if (value === undefined || value === null || value === '') return undefined
  return Number(value)
}

function timestampIso(value: SerializedTimestamp | string): string {
  if (typeof value === 'string') return value
  const seconds = value._seconds ?? value.seconds
  return seconds === undefined
    ? new Date(0).toISOString()
    : new Date(seconds * 1000).toISOString()
}

function listQuery(params: GetListParams): ListDiscussionCommentsQuery {
  const filter = params.filter ?? {}
  return buildCommentQuery({
    limit: params.pagination?.perPage ?? 20,
    moderationStatus: optionalText(filter.moderationStatus) as ModerationStatus,
    visibilityStatus: optionalText(filter.visibilityStatus) as VisibilityStatus,
    policyReason: optionalText(filter.policyReason),
    mediaType: optionalNumber(filter.bookId) === undefined ? undefined : 'book',
    bookId: optionalNumber(filter.bookId),
    chapterId: optionalNumber(filter.chapterId),
    authorId: optionalNumber(filter.authorId),
    exactCommentId: optionalText(filter.exactCommentId),
  })
}

function reportListQuery(params: GetListParams): ListDiscussionReportsQuery {
  const filter = params.filter ?? {}
  return buildReportQuery({
    limit: params.pagination?.perPage ?? 20,
    status: optionalText(filter.status) as ListDiscussionReportsQuery['status'],
    reason: optionalText(filter.reason) as ListDiscussionReportsQuery['reason'],
    mediaType: optionalNumber(filter.bookId) === undefined ? undefined : 'book',
    bookId: optionalNumber(filter.bookId),
    reporterUserId: optionalNumber(filter.reporterUserId),
    reportedAuthorId: optionalNumber(filter.reportedAuthorId),
  })
}

function listRecord(
  item: DiscussionCommentListItem,
): DiscussionCommentListRecord {
  return {
    id: discussionCommentIdentityKey(item.comment),
    createdAt: timestampIso(item.comment.createdAt),
    bookTitle: item.bookTitle || `Book #${item.comment.bookId}`,
    chapterTitle: item.chapterTitle || `Chapter #${item.comment.chapterId}`,
    commentBody: item.comment.body,
    authorId: item.comment.authorId,
    authorName:
      item.authorName ||
      item.comment.authorSnapshot?.name ||
      `User #${item.comment.authorId}`,
    reportCount: item.reportCount ?? item.unresolvedReportCount ?? 0,
    moderationStatus: item.comment.moderationStatus,
    visibilityStatus: item.comment.visibilityStatus,
    source: item,
  }
}

function reportListRecord(
  item: DiscussionReportListItem,
): DiscussionReportListRecord {
  const commentIdentity = discussionCommentIdentityKey(item.comment)
  return {
    id: `${commentIdentity}:${encodeURIComponent(item.report.reportId)}`,
    commentIdentity,
    createdAt: timestampIso(item.report.createdAt),
    bookTitle: item.bookTitle || `Book #${item.comment.bookId}`,
    chapterTitle: item.chapterTitle || `Chapter #${item.comment.chapterId}`,
    reporterId: item.report.reporterUserId,
    reporterName: item.reporterName || `User #${item.report.reporterUserId}`,
    reason: item.report.reason,
    evidence:
      item.report.reportedBodySnapshot ??
      item.report.details ??
      'Evidence unavailable',
    status: item.report.status,
    source: item,
  }
}

export function withChapterDiscussionDataProvider(
  baseProvider: DataProvider,
): DataProvider {
  const cursorPages = new Map<string, Map<number, string | undefined>>()

  return {
    ...baseProvider,
    getList: async (
      resource: string,
      params: GetListParams,
    ): Promise<GetListResult> => {
      if (
        resource !== CHAPTER_DISCUSSION_COMMENTS_RESOURCE &&
        resource !== CHAPTER_DISCUSSION_REPORTS_RESOURCE
      ) {
        return baseProvider.getList(resource, params)
      }

      const page = params.pagination?.page ?? 1
      const query =
        resource === CHAPTER_DISCUSSION_COMMENTS_RESOURCE
          ? listQuery(params)
          : reportListQuery(params)
      const cursorKey = `${resource}:${JSON.stringify(query)}`
      let cursors = cursorPages.get(cursorKey)
      if (!cursors) {
        cursors = new Map([[1, undefined]])
        cursorPages.set(cursorKey, cursors)
      }
      if (!cursors.has(page)) {
        throw new Error('Please navigate discussion pages sequentially.')
      }

      const cursor = cursors.get(page)
      const response =
        resource === CHAPTER_DISCUSSION_COMMENTS_RESOURCE
          ? await discussionCsApi.listComments({
              ...(query as ListDiscussionCommentsQuery),
              cursor,
            })
          : await discussionCsApi.listReports({
              ...(query as ListDiscussionReportsQuery),
              cursor,
            })
      if (response.nextCursor) {
        cursors.set(page + 1, response.nextCursor)
      } else {
        cursors.delete(page + 1)
      }
      for (const cachedPage of Array.from(cursors.keys())) {
        if (cachedPage > page + 1) cursors.delete(cachedPage)
      }

      return {
        data:
          resource === CHAPTER_DISCUSSION_COMMENTS_RESOURCE
            ? (response.items as DiscussionCommentListItem[]).map(listRecord)
            : (response.items as DiscussionReportListItem[]).map(
                reportListRecord,
              ),
        pageInfo: {
          hasPreviousPage: page > 1,
          hasNextPage: Boolean(response.nextCursor),
        },
      }
    },
  } as DataProvider
}
